import React from "react";
import styles from "../../../../assets/styles/components/Suggestion.module.scss";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------
export type AddImageAssetSuggestion = {
    ad_group_id: string;
    ad_group_name: string;
    current_asset_count: number;
    recommended_asset_count: number;
    reason?: string;
};

export type AddImageAssetUploadValue = {
    fileName: string;
    dataUrl: string;
};

const ASSET_SPECS = [
    { ratio: 1.91, minW: 600, minH: 314, suffix: "landscape" },
    { ratio: 1.0, minW: 300, minH: 300, suffix: "square" },
    { ratio: 0.8, minW: 400, minH: 600, suffix: "portrait" },
];

const loadImage = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("画像の読み込みに失敗しました"));
        };
        img.src = url;
    });

const buildCroppedDataUrl = async (file: File): Promise<AddImageAssetUploadValue> => {
    const img = await loadImage(file);
    const srcW = img.naturalWidth;
    const srcH = img.naturalHeight;
    const srcRatio = srcW / srcH;

    const spec = ASSET_SPECS.reduce((best, cur) => {
        const bestDiff = Math.abs(srcRatio - best.ratio);
        const curDiff = Math.abs(srcRatio - cur.ratio);
        return curDiff < bestDiff ? cur : best;
    });

    let cropW = srcW;
    let cropH = srcH;
    let sx = 0;
    let sy = 0;

    if (srcRatio > spec.ratio) {
        cropW = srcH * spec.ratio;
        sx = (srcW - cropW) / 2;
    } else if (srcRatio < spec.ratio) {
        cropH = srcW / spec.ratio;
        sy = (srcH - cropH) / 2;
    }

    const scale = Math.max(spec.minW / cropW, spec.minH / cropH, 1);
    const outW = Math.round(cropW * scale);
    const outH = Math.round(cropH * scale);

    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
        throw new Error("画像変換に失敗しました");
    }
    ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, outW, outH);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    const fileName = `${file.name.replace(/\.[^.]+$/, "")}_${spec.suffix}.jpg`;
    return { fileName, dataUrl };
};

type Props = {
    suggestions: AddImageAssetSuggestion[];
    formData: Record<string, AddImageAssetUploadValue | null>;
    onChange: (id: string, fileData: AddImageAssetUploadValue | null) => void;
};

// -----------------------------------------------------------------------------
// Component: AddImageAssetForm
// -----------------------------------------------------------------------------
const AddImageAssetForm: React.FC<Props> = ({ suggestions, formData, onChange }) => {
    return (
        <div className={styles.imageAssetFormContainer}>
            {suggestions.map((sg) => {
                const uploadedFile = formData[sg.ad_group_id];

                return (
                    <section key={sg.ad_group_id} className={styles.campaignCard}>
                        <div className={styles.campaignName}>{sg.ad_group_name}</div>

                        {sg.reason && (
                            <div className={styles.reasonBar}>
                                <strong>Aibex提案理由:</strong> {sg.reason}
                            </div>
                        )}

                        <div className={styles.suggestionBox}>
                            {/* Before */}
                            <div className={styles.beforePanel}>
                                <div className={styles.panelTitle}>現在の設定</div>
                                <div className={styles.displayRow}>
                                    <div className={`${styles.statusBadge} ${styles.bgNeutral}`}>
                                        画像素材: {sg.current_asset_count}枚 (推奨{sg.recommended_asset_count}枚)
                                    </div>
                                </div>
                                <div className={styles.subValue}>
                                    現状、画像素材が不足しています。
                                </div>
                            </div>

                            {/* Arrow */}
                            <div className={styles.arrowIcon}>→</div>

                            {/* After: Upload UI */}
                            <div className={styles.afterPanel}>
                                <div className={styles.panelTitle}>新しい画像を追加</div>
                                <div className={styles.uploadArea}>
                                    {uploadedFile ? (
                                        <div className={styles.uploadedPreview}>
                                            <span>{uploadedFile.fileName}</span>
                                            <button type="button" onClick={() => onChange(sg.ad_group_id, null)}>
                                                削除
                                            </button>
                                        </div>
                                    ) : (
                                        <label className={styles.uploadBox}>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className={styles.hiddenInput}
                                                onChange={async (e) => {
                                                    if (e.target.files && e.target.files.length > 0) {
                                                        const file = e.target.files[0];
                                                        try {
                                                            const converted = await buildCroppedDataUrl(file);
                                                            onChange(sg.ad_group_id, converted);
                                                        } catch (err) {
                                                            console.error("[AddImageAssetForm] image convert failed", err);
                                                        }
                                                    }
                                                }}
                                            />
                                            <div className={styles.uploadPlaceholder}>
                                                <span>クリックして画像をアップロード</span>
                                            </div>
                                        </label>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default AddImageAssetForm;
